// Base API client — injects auth token, handles 401 → token refresh → retry

import { useAuthStore } from '../store/auth'

const BASE_URL = '/api/v1'

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function parseError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } }
    return new ApiError(res.status, body.error?.code ?? 'UNKNOWN', body.error?.message ?? 'Request failed')
  } catch {
    return new ApiError(res.status, 'PARSE_ERROR', 'Request failed')
  }
}

async function refreshAccessToken(): Promise<string | null> {
  const { refreshToken, setTokens, clearAuth } = useAuthStore.getState()
  if (!refreshToken) return null

  try {
    const res = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
    if (!res.ok) {
      clearAuth()
      return null
    }
    const body = (await res.json()) as {
      data: { accessToken: string; refreshToken: string; user: unknown }
    }
    setTokens(body.data.accessToken, body.data.refreshToken, body.data.user)
    return body.data.accessToken
  } catch {
    clearAuth()
    return null
  }
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const { accessToken } = useAuthStore.getState()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  let res = await fetch(`${BASE_URL}${path}`, { ...options, headers })

  // On 401 with an existing token, try refresh once then retry
  if (res.status === 401 && accessToken) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`
      res = await fetch(`${BASE_URL}${path}`, { ...options, headers })
    }
  }

  if (!res.ok) {
    throw await parseError(res)
  }

  if (res.status === 204) return undefined as T

  return res.json() as Promise<T>
}

/**
 * Like apiFetch, but for binary responses (e.g. the upload proxy) instead of
 * JSON. Returns the raw bytes + content type rather than a Blob — Blob
 * objects produced by fetch polyfills aren't always interchangeable with the
 * Blob implementation other Web APIs (e.g. FileReader) expect, depending on
 * the runtime, while `arrayBuffer()` has no such cross-implementation gap.
 */
export async function apiFetchBlob(path: string): Promise<{ bytes: ArrayBuffer; contentType: string }> {
  const { accessToken } = useAuthStore.getState()

  const headers: Record<string, string> = {}
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  let res = await fetch(`${BASE_URL}${path}`, { headers })

  if (res.status === 401 && accessToken) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`
      res = await fetch(`${BASE_URL}${path}`, { headers })
    }
  }

  if (!res.ok) {
    throw await parseError(res)
  }

  return { bytes: await res.arrayBuffer(), contentType: res.headers.get('content-type') ?? 'application/octet-stream' }
}
